/**
 * Settings → Microsoft Sentinel (Enterprise): the data collection rule the platform writes the
 * organisation's security-worthy audit rows to, as this deployment's managed identity or the AIOE
 * API application (docs/agent365.md; Azure refuses a blueprint any role assignment).
 */

import { Button } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ErrorNote, inputClass, PlainPanel, StatusMark } from './ui.tsx';

export function SentinelPanel() {
  const view = useLoad(() => routes.sentinel(), []);
  const [endpoint, setEndpoint] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await f();
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const v = view.data;
  return (
    <PlainPanel title="Microsoft Sentinel">
      <ErrorNote error={error ?? view.error} />
      {v ? (
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-y-1.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
          <dt className="text-muted">Status</dt>
          <dd>{v.connected ? <StatusMark state="online" label="Connected" /> : <StatusMark state="idle" label="Not connected" />}</dd>
          {v.connected ? (
            <>
              <dt className="text-muted">Rule</dt>
              <dd className="mono break-all">
                {v.ruleId} <span className="text-muted">{v.stream}</span>
              </dd>
              <dt className="text-muted">Endpoint</dt>
              <dd className="mono break-all">{v.endpoint}</dd>
              <dt className="text-muted">Rows sent</dt>
              <dd className="tabular-nums">
                {v.rowsSent}
                {v.lastSentAt ? <span className="text-muted"> · last {timeAgo(v.lastSentAt)}</span> : null}
              </dd>
              {v.lastError ? (
                <>
                  <dt className="text-muted">Last problem</dt>
                  <dd className="text-danger">{v.lastError}</dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>
      ) : null}
      {v && !v.connected ? (
        <div className="mt-3 grid gap-3">
          <p className="text-sm text-muted">Audit rows worth a detection (refusals, errors, every data-protection decision) are sent to your Sentinel workspace as rows of MojoUpSecurityEvents_CL, each with its place and hash in the audit chain. Deploy the shipped table, rule and detections first, naming this deployment’s managed identity (or, outside Azure, the AIOE API application) as the sender: it gets the Monitoring Metrics Publisher role on the rule.</p>
          <label className="grid gap-1 text-xs text-muted">
            Logs ingestion endpoint
            <input className={`${inputClass} mono`} value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://mojoup-dce-xxxx.australiaeast-1.ingest.monitor.azure.com" spellCheck={false} />
          </label>
          <label className="grid gap-1 text-xs text-muted">
            Data collection rule immutable id
            <input className={`${inputClass} mono`} value={ruleId} onChange={(e) => setRuleId(e.target.value)} placeholder="dcr-…" spellCheck={false} />
          </label>
          <div>
            <Button size="sm" isPending={busy} isDisabled={!endpoint.trim() || !ruleId.trim()} onPress={() => void act(() => routes.connectSentinel({ endpoint: endpoint.trim(), ruleId: ruleId.trim(), stream: 'Custom-MojoUpSecurityEvents' }))}>
              Connect
            </Button>
          </div>
        </div>
      ) : v?.connected ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" isPending={busy} onPress={() => void act(() => routes.disconnectSentinel())}>
            Disconnect
          </Button>
          <span className="text-xs text-muted">A backfill: Audit, export, format Sentinel.</span>
        </div>
      ) : null}
    </PlainPanel>
  );
}
